import { moneyFromDecimalString } from '@ibe/utils';
import { z } from 'zod';

/**
 * The Daily Rates module's contracts.
 *
 * Rates cross the API as minor-unit strings; the operator sees and edits
 * decimal amounts. The schemas here keep the two separate.
 */

/** A rate plan as the picker needs it. */
export const ratePlanOption = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  currency: z.string().optional(),
});

export type RatePlanOption = z.infer<typeof ratePlanOption>;

/** A room type as the picker needs it. */
export const roomTypeOption = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
});

export type RoomTypeOption = z.infer<typeof roomTypeOption>;

function dateString(label: string): z.ZodType<string, string> {
  return z
    .string()
    .min(1, `${label} is required`)
    .regex(/^\d{4}-\d{2}-\d{2}$/u, `${label} must be a date in YYYY-MM-DD format`);
}

/** One published rate for one room type on one night. */
export const dailyRate = z.object({
  roomTypeId: z.string(),
  ratePlanId: z.string(),
  date: z.string(),
  baseRate: z.string(),
  currency: z.string(),
  closed: z.boolean(),
  taxApplied: z.boolean(),
  taxId: z.string().nullable(),
});

export type DailyRate = z.infer<typeof dailyRate>;

/** A tax the property charges, as the tax picker needs it. */
export const tax = z.object({
  id: z.string(),
  name: z.string(),
  rate: z.string().nullable(),
  amount: z.string().nullable(),
});

export type Tax = z.infer<typeof tax>;

function baseRateField(currency: string): z.ZodType<string, string> {
  return z
    .string()
    .trim()
    .min(1, 'A base rate is required')
    .superRefine((value, ctx) => {
      try {
        moneyFromDecimalString(value, currency);
      } catch (error) {
        ctx.addIssue({
          code: 'custom',
          message: error instanceof Error ? error.message : 'Invalid amount',
          path: ['baseRate'],
        });
      }
    });
}

export interface DailyRateFormValues {
  readonly baseRate: string;
  readonly closed: boolean;
  readonly taxApplied: boolean;
  readonly taxId: string;
}

/**
 * The form for a single night or a whole range.
 *
 * Currency is supplied at call time because the rate plan determines it, not
 * the form. `taxId` stays an empty string when no tax is chosen, and the
 * payload builder turns it into `null`.
 */
export function dailyRateFormSchema(
  currency: string,
): z.ZodType<DailyRateFormValues, DailyRateFormValues> {
  return z
    .object({
      baseRate: baseRateField(currency),
      closed: z.boolean(),
      taxApplied: z.boolean(),
      taxId: z.string(),
    })
    .refine(
      (values) => !values.taxApplied || values.taxId !== '',
      'Choose a tax when tax is applied',
    );
}

export interface BulkRateFormValues {
  readonly from: string;
  readonly to: string;
  readonly baseRate: string;
  readonly closed: boolean;
  readonly taxApplied: boolean;
  readonly taxId: string;
}

export function bulkRateFormSchema(
  currency: string,
): z.ZodType<BulkRateFormValues, BulkRateFormValues> {
  return z
    .object({
      from: dateString('From'),
      to: dateString('To'),
      baseRate: baseRateField(currency),
      closed: z.boolean(),
      taxApplied: z.boolean(),
      taxId: z.string(),
    })
    .refine((values) => values.to >= values.from, {
      message: 'The range ends before it starts',
      path: ['to'],
    })
    .refine(
      (values) => !values.taxApplied || values.taxId !== '',
      'Choose a tax when tax is applied',
    );
}

/** The payload sent to `PUT /v1/rates`. */
export interface SetRatesPayload {
  readonly propertyId: string;
  readonly ratePlanId: string;
  readonly roomTypeId: string;
  readonly from: string;
  readonly to: string;
  readonly baseRate: string;
  readonly closed: boolean;
  readonly taxApplied: boolean;
  readonly taxId: string | null;
}
